import {
    COMPETITIONS_CATEGORY_FAILURE,
    COMPETITIONS_CATEGORY_REQUEST,
    COMPETITIONS_CATEGORY_SUCCESS,
    COMPETITION_CATEGORY_PATCH_SUCCESS,
    COMPETITIONS_MAKE_DRAW_SUCCESS,
    COMPETITIONS_DELETE_DRAW_SUCCESS,
    GENERATE_ALL_DRAWS_SUCCESS,
    DRAW_SWAP_SUCCESS,
    DRAW_RESET_SUCCESS,
    DRAW_APPROVE_SUCCESS,
    DRAW_REOPEN_SUCCESS,
    OFFICIAL_RESULTS_CONFIRM_SUCCESS,
    CORRECTION_RESET_RESULTS_SUCCESS,
    CORRECTION_REGENERATE_OFFICIAL_RESULTS_SUCCESS,
    CATEGORY_NOT_HELD_SUCCESS,
    COMPETITION_RESULT_WINNER_SUCCESS,
    COMPETITION_RESULT_PATCH_SUCCESS,
    SCORE_ENTRY_SUCCESS,
    SCORE_ROUND_COMPLETE_SUCCESS,
    SCORE_ROUND_REOPEN_SUCCESS,
    POINTS_STAGE_RESET_SUCCESS,
    CORRECTION_START_SUCCESS,
    CORRECTION_FINISH_SUCCESS,
    PARTICIPANT_POST_SUCCESS,
    PARTICIPANT_DELETE_SUCCESS,
    MSAC_CONSOLIDATED_SAVE_SUCCESS,
} from 'actions';

const initialState = {
    data: [],
    isFetched: [],
    isFetching: [],
};

/**
 * THE COMPETITION-CATEGORY ROWS the category list reads its three numbers from:
 * entered athletes, fights in the draw, estimated minutes.
 *
 * ── Why a draw invalidates this ──────────────────────────────────────────
 *
 * `fightCount` and `estimatedDurationMinutes` are NULL until a draw exists and
 * are computed by the backend from it. Nothing here used to notice a draw
 * being made, so the list went on showing «—» until the page was reloaded by
 * hand — which is the one thing a React app should never need (reported
 * 24 Sep 2026).
 *
 * Every action that CHANGES a draw drops the competition from `isFetched`, and
 * the list's effect re-reads because of it. Deliberately the whole list rather
 * than one row: «Створити всі жеребкування» changes thirty at once, and a
 * per-row patch would need the backend to answer with rows it does not
 * currently return.
 *
 * ── …and so does CONFIRMING or UN-CONFIRMING a result ────────────────────
 *
 * `closed` is the same row's flag, and it is what the green tick beside a
 * category is drawn from. Confirming an official result sets it; Reset
 * Category Results in the correction workflow clears it
 * ({@see OfficialResultsConfirmationService} and
 * {@see CategoryCorrectionService} are the only two writers).
 *
 * Neither used to reach this cache. Both actions re-read the category
 * OVERVIEW, which is a different payload, so the tick was stale in both
 * directions: it did not appear when a result was confirmed and did not go
 * when the results were reset — until the page was reloaded by hand. Found at
 * exactly that moment: «скидаю результати категорії, іконка має зникнути»
 * (30 Sep 2026).
 */
const withoutCompetition = (ids, competitionId) => ids.filter(id => id !== competitionId);

/**
 * The competition a draw action was performed on.
 *
 * EVERY draw action carries `competition` — two of them were given it for
 * this, because `response.data` is the draw's rows and a cache keyed on the
 * competition cannot invalidate what it cannot identify. Both the id and the
 * whole object appear in the codebase, so both are accepted; nothing else is
 * guessed at.
 */
const competitionOf = action => {
    // A patched result row carries its competition itself, as {id}.
    const competition = action.competition
        || (action.result && action.result.competition ? action.result.competition : null);

    if ('string' === typeof competition) {
        return competition;
    }

    return competition && competition.id ? competition.id : null;
};

export const competitionCategoryListReducer = (state = initialState, action) => {
    const {type, data: responseData, competition, result} = action;
    const {data, isFetched, isFetching} = state;

    switch (type) {
        case COMPETITION_CATEGORY_PATCH_SUCCESS: {
            return {
                ...state,
                data: data.map(d => d.id === result.id ? result : d)
            }
        }

        // The consolidated report's choice, as the server answered it — only
        // the flag on this competition's rows changes (20 Oct 2026).
        case MSAC_CONSOLIDATED_SAVE_SUCCESS: {
            const chosen = new Set(action.categories || []);
            return {
                ...state,
                data: data.map(row => (row.competition && row.competition.id === competition && row.category
                    ? { ...row, consolidatedReport: chosen.has(row.category.id) }
                    : row)),
            };
        }

        case COMPETITIONS_CATEGORY_SUCCESS: {return {
            ...state,
            // REPLACE this competition's rows, never append. Appending was
            // harmless while the list was fetched exactly once per competition
            // and never again; the moment anything re-reads it — which
            // invalidation now makes routine — every row appears twice and
            // `find()` keeps answering with the stale first copy.
            isFetched: [...withoutCompetition(isFetched, competition), competition],
            isFetching: withoutCompetition(isFetching, competition),
            data: [
                ...data.filter(d => !d.competition || d.competition.id !== competition),
                ...responseData,
            ],
        }}

        case COMPETITIONS_CATEGORY_REQUEST: {return {
            ...state,
            isFetching: [...withoutCompetition(isFetching, competition), competition],
        }}

        case COMPETITIONS_CATEGORY_FAILURE: {return {
            ...state,
            isFetching: withoutCompetition(isFetching, competition),
        }}

        // A RESULT WAS CONFIRMED OR UN-CONFIRMED — `closed`, and with it the
        // green tick, is now wrong.
        case OFFICIAL_RESULTS_CONFIRM_SUCCESS:
        case CORRECTION_RESET_RESULTS_SUCCESS:
        case CORRECTION_REGENERATE_OFFICIAL_RESULTS_SUCCESS:
        // …OR A CATEGORY WAS RECORDED AS NEVER HELD, or that was taken back.
        // `notHeld` is what the red «not held» mark is drawn from, and it also decides
        // whether the row belongs in the «Не підтверджені» filter at all.
        case CATEGORY_NOT_HELD_SUCCESS:
        // A RESULT WAS WRITTEN, or a correction opened or closed — the list's
        // status mark (`lifecycleState`/`progressState`: in progress, awaiting
        // confirmation, under correction) is now wrong. Picking a winner on
        // the bracket left the mark as it was until the page was reloaded
        // (3 Oct 2026).
        case COMPETITION_RESULT_WINNER_SUCCESS:
        case COMPETITION_RESULT_PATCH_SUCCESS:
        case SCORE_ENTRY_SUCCESS:
        case SCORE_ROUND_COMPLETE_SUCCESS:
        case SCORE_ROUND_REOPEN_SUCCESS:
        case POINTS_STAGE_RESET_SUCCESS:
        case CORRECTION_START_SUCCESS:
        case CORRECTION_FINISH_SUCCESS:
        // AN ENTRY WAS ADDED OR REMOVED — the athlete count is wrong, a drawn
        // category may now be OUTDATED (`drawOutdated`), and removing one from
        // a drawn category cancelled its draw (4 Oct 2026).
        case PARTICIPANT_POST_SUCCESS:
        case PARTICIPANT_DELETE_SUCCESS:
        // A round robin's table re-ranked the category (RoundRobinPanel).
        case 'ROUND_ROBIN_SAVED':
        // A DRAW CHANGED — the fight count and the estimate are now wrong.
        case COMPETITIONS_MAKE_DRAW_SUCCESS:
        case COMPETITIONS_DELETE_DRAW_SUCCESS:
        case GENERATE_ALL_DRAWS_SUCCESS:
        case DRAW_SWAP_SUCCESS:
        case DRAW_RESET_SUCCESS:
        case DRAW_APPROVE_SUCCESS:
        case DRAW_REOPEN_SUCCESS: {
            const changed = competitionOf(action);

            // No competition in the payload is not a reason to drop the whole
            // cache: the list would refetch for every competition the user has
            // open, which is a storm rather than a refresh.
            if (null === changed) {
                return state;
            }

            return {
                ...state,
                isFetched: withoutCompetition(isFetched, changed),
            };
        }

        default: {return state}
    }
};
